<template>
	<InviteUsersInputModal
		ref="inviteUsersInputModal"
		@success="handleInviteSuccess"
	/>
	<ManagedInviteResultModal ref="managedInviteResultModal" />
</template>

<script setup lang="ts">
	/**
	 * InviteUserModal - Wrapper component that coordinates the invite flow
	 *
	 * This is the top-level component that should be used by other parts of the app
	 * when they need to invoke the user invitation flow. It orchestrates the two-step
	 * process: collecting invite information and showing the results for managed accounts.
	 */
	import { ref } from "vue";
	import InviteUsersInputModal from "@/components/feature/auth/invite/InviteUserModal/InviteUsersInputModal.vue";
	import ManagedInviteResultModal from "@/components/feature/auth/invite/InviteUserModal/ManagedInviteResultModal.vue";
	import type { BulkInviteResultDTO } from "seedgreen-shared/dtos/InviteDTO";

	const inviteUsersInputModal =
		ref<InstanceType<typeof InviteUsersInputModal>>();
	const managedInviteResultModal =
		ref<InstanceType<typeof ManagedInviteResultModal>>();

	/**
	 * Opens the invite user modal to start the flow
	 */
	const open = () => {
		inviteUsersInputModal.value?.openModal();
	};

	/**
	 * Handles successful invite creation.
	 * If mode is "managed" and there are any results (success or failure), show the links modal.
	 * Otherwise, the flow is complete.
	 */
	const handleInviteSuccess = (
		mode: "email" | "managed",
		results: BulkInviteResultDTO,
	) => {
		if (mode === "managed" && results.totalProcessed > 0) {
			// Show the invite results modal with full results (successes and failures)
			managedInviteResultModal.value?.openModal(results);
		}
		// For email mode or no processed invites, the flow is complete
	};

	defineExpose({
		open,
	});
</script>
